import { Circ, Expo, TimelineLite, TweenMax } from "gsap";
import * as HLF from "html-living-framework";
import * as PIXI from "pixi.js";
import { Application } from "../Application";
import {Ease} from "../core/Animatable";
import {Container} from "../core/Container";
import {array} from "../core/Reflection";
import {Sprite} from "../core/Sprite";
import LightsOutGameModel from "../data/LightsOutGameModel";
import { LightsOutObjectType } from "../data/LightsOutObjectType";
import * as VO from "../data/VO";
import { AnimatedLightsOutObject } from "../gameobjects/AnimatedLightsOutObject";
import { Button } from "../gameobjects/Button";
import { Guitar } from "../gameobjects/Guitar";
import { LightsOutObject } from "../gameobjects/LightsOutObject";
import { Piano } from "../gameobjects/Piano";
import { Radio } from "../gameobjects/Radio";
import { StaticLightsOutObject } from "../gameobjects/StaticLightsOutObject";
import {LightsOutElmoLivingRoomLayout} from "../layout/LightsOutElmoLivingRoomLayout";
import {AbstractGameState} from "./AbstractGameState";
import { AbstractLightsOutGameState } from "./AbstractLightsOutGameState";

export class LightsOutElmoLivingRoomScreen extends AbstractLightsOutGameState {

    public static readonly NAME = "LightsOutElmoLivingRoomScreen";

    public readonly bg = this.addChild(new Sprite());
    public readonly piano = this.addChild(new Piano({
        type: LightsOutObjectType.EasterEgg,
        spriteSheet: "lightsout-livingroom-1",
        frames: 2,
        loopCount: 3,
    }));
    public readonly ceilingLight = this.addChild(new StaticLightsOutObject({type: LightsOutObjectType.Light}));
    public readonly globe = this.addChild(new AnimatedLightsOutObject({
        type: LightsOutObjectType.EasterEgg,
        spriteSheet: "lightsout-livingroom-1",
        frames: 10,
        loopCount: 2,
    }));
    public readonly guitar = this.addChild(new Guitar({type: LightsOutObjectType.EasterEgg}));
    public readonly lamp = this.addChild(new StaticLightsOutObject({type: LightsOutObjectType.Light}));
    public readonly lightSwitch = this.addChild(new StaticLightsOutObject({type: LightsOutObjectType.Light}));
    public readonly pianoSeat = this.addChild(new StaticLightsOutObject({type: LightsOutObjectType.EasterEgg}));
    public readonly robot = this.addChild(new StaticLightsOutObject({type: LightsOutObjectType.Electricity}));
    public readonly tablet = this.addChild(new StaticLightsOutObject({type: LightsOutObjectType.Electricity}));
    public readonly tv = this.addChild(new StaticLightsOutObject({type: LightsOutObjectType.Electricity}));
    public readonly radio = this.addChild(new Radio());
    public readonly ceilinglightGlow = this.addChild(new Sprite());
    public readonly lampGlow = this.addChild(new Sprite());
    protected analyticsScreenName: string = "5c Lights Out_Living Room Screen";

    protected readonly roomID = "LivingRoom";
    protected readonly roomNameVO = VO.LO_ROOM01;

    protected _subscriptions: any[] = [];

    protected itemsToTurnOff: LightsOutObject[] = [
        this.lightSwitch,
        this.lamp,
        this.radio,
        this.robot,
        this.tablet,
        this.tv,
    ];

    public init(pSize: PIXI.Point, pData: {}) {
        LightsOutElmoLivingRoomLayout.call(this);
        super.init(pSize, pData);

        this.tv.lightsOutSFX = "lights-out-electronics-button";
        this.lamp.lightsOutSFX = "lights-out-lamp";
        this.robot.lightsOutSFX = "lights-out-robot";
        this.tablet.lightsOutSFX = "lights-out-electronics-button";
        this.radio.lightsOutSFX = "lights-out-electronics-button";
        this.ceilingLight.lightsOutSFX = "lights-out-light-switch";
        this.guitar.clickSfx = "lights-out-guitar";
        this.guitar.clickSfxCanOverlap = false;
        this.globe.clickSfx = "lights-out-globe";
        this.globe.clickSfxCanOverlap = false;
        this.piano.clickSfx = "lights-out-piano";
        this.piano.clickSfxCanOverlap = false;

        this.makeObjectClickable(this.lamp);
        this.makeObjectClickable(this.radio);
        this.makeObjectClickable(this.robot);
        this.makeObjectClickable(this.tablet);
        this.makeObjectClickable(this.tv);
        this.makeObjectClickable(this.globe);
        this.makeObjectClickable(this.piano);
        this.makeObjectClickable(this.guitar);
        this.makeSwitch(this.lightSwitch, this.ceilingLight);

        // randomize objects
        HLF.ArrayUtils.shuffle(this.itemsToTurnOff);

        // move already played objects to end of array
        this.itemsToTurnOff.sort((a, b) => LightsOutGameModel.rooms.LivingRoom.itemsLeftOn.indexOf(a.name));
        // offset the array by one so it's not just flip flopping between two sets of three
        this.itemsToTurnOff.push(this.itemsToTurnOff.shift()!);

        LightsOutGameModel.rooms.LivingRoom.itemsLeftOn = [];

        // disable all but three of them
        for (let i = 0; i < this.itemsToTurnOff.length; i++) {
            if (i < 3) {
                this.itemsToTurnOff[i].TurnOn();
                this.itemsToTurnOff[i].Highlight();
                LightsOutGameModel.rooms.LivingRoom.itemsLeftOn.push(this.itemsToTurnOff[i].name);
            }
            else {
                this.itemsToTurnOff[i].TurnOn({animate: false}); // hacky fix for lights glow
                this.itemsToTurnOff[i].TurnOff({animate: false});
        }
        }

        HLF.PixiUtils.sendToFront(this.homeButton);
        HLF.PixiUtils.sendToFront(this.elmo);
    }

    public animateIn(pOnComplete: () => void) {
        super.animateIn(pOnComplete);
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);
        this.position.set((pSize.x - 1664) / 2, 0);
    }

    public update(pDeltaTime: number) {
        super.update(pDeltaTime);
    }

    public destroy() {
        super.destroy();
        for (const subscription of this._subscriptions) {
            PubSub.unsubscribe(subscription);
        }
    }

    protected onItemTurnedOn(pTopic: "item turned on", pItem: LightsOutObject) {
        super.onItemTurnedOn(pTopic, pItem);
        if (pItem === this.robot) {
            TweenMax.fromTo(this.robot, 0.5, {pixi: {x: this.robot.x}}, {pixi: {x: this.robot.x - 80}}).yoyo(true).repeat(-1).repeatDelay(0.5);
        }
    }

    protected onItemTurnedOff(pTopic: "item turned off", pItem: LightsOutObject) {
        super.onItemTurnedOff(pTopic, pItem);
        if (pItem === this.robot) {
            TweenMax.killTweensOf(this.robot);
        }
    }
}
